import React, { useEffect, useMemo, useState } from "react";
import {
  FaChartPie,
  FaFileExcel,
  FaFilter,
  FaMoneyBillWave,
  FaSearch,
  FaTimes,
  FaUserTie,
  FaWallet,
} from "react-icons/fa";
import * as XLSX from "xlsx";
import api from "../../api";

const ExpenseReportPage = () => {
  const [summary, setSummary] = useState({
    manual_expense: 0,
    salary_expense: 0,
    total_expense: 0,
  });

  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    fromDate: "",
    toDate: "",
    type: "all",
    title: "",
  });
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchSummary = async () => {
    try {
      const res = await api.get("/expenses/summary");
      if (res.data.status) {
        setSummary(res.data);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const fetchTransactions = async () => {
    try {
      const res = await api.get("/expenses/details");
      if (res.data.status) {
        setTransactions(res.data.data || []);
      }
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchSummary();
      await fetchTransactions();
      setLoading(false);
    };

    loadData();
  }, []);

  const typeOptions = useMemo(() => {
    const values = transactions
      .map((item) => item.expense_type)
      .filter(Boolean)
      .map((item) => String(item));
    return [...new Set(values)];
  }, [transactions]);

  const filteredTransactions = useMemo(() => {
    const titleNeedle = filters.title.trim().toLowerCase();
    const fromTime = filters.fromDate ? new Date(filters.fromDate).setHours(0, 0, 0, 0) : null;
    const toTime = filters.toDate ? new Date(filters.toDate).setHours(23, 59, 59, 999) : null;

    return transactions.filter((item) => {
      const itemType = String(item.expense_type || "");
      const itemTitle = String(item.title || "").toLowerCase();
      const dateValue = item.trans_date || item.expense_date || item.date || "";
      const itemTime = dateValue ? new Date(dateValue).getTime() : null;

      if (filters.type !== "all" && itemType !== filters.type) return false;
      if (titleNeedle && !itemTitle.includes(titleNeedle)) return false;
      if (fromTime && itemTime && itemTime < fromTime) return false;
      if (toTime && itemTime && itemTime > toTime) return false;
      if ((fromTime || toTime) && !itemTime) return false;
      return true;
    });
  }, [filters, transactions]);

  const visibleTransactions = useMemo(() => {
    if (rowsPerPage === "all") return filteredTransactions;
    return filteredTransactions.slice(0, Number(rowsPerPage));
  }, [filteredTransactions, rowsPerPage]);

  const filteredManual = filteredTransactions
    .filter((item) => !String(item.expense_type || "").toLowerCase().includes("salary"))
    .reduce((sum, item) => sum + Number(item.amount || 0), 0);

  const filteredSalary = filteredTransactions
    .filter((item) => String(item.expense_type || "").toLowerCase().includes("salary"))
    .reduce((sum, item) => sum + Number(item.amount || 0), 0);

  const filteredTotal = filteredTransactions.reduce(
    (sum, item) => sum + Number(item.amount || 0),
    0
  );

  const hasTransactionData = transactions.length > 0;
  const displayManual = hasTransactionData ? filteredManual : Number(summary.manual_expense || 0);
  const displaySalary = hasTransactionData ? filteredSalary : Number(summary.salary_expense || 0);
  const displayTotal = hasTransactionData ? filteredTotal : Number(summary.total_expense || 0);

  const cards = [
    {
      label: "Manual Expense",
      value: displayManual,
      icon: <FaWallet />,
      color: "#2563eb",
      bg: "#eff6ff",
      note: "Filtered manual entries",
    },
    {
      label: "Salary Expense",
      value: displaySalary,
      icon: <FaUserTie />,
      color: "#f59e0b",
      bg: "#fffbeb",
      note: "Filtered payroll expenses",
    },
    {
      label: "Total Expense",
      value: displayTotal,
      icon: <FaMoneyBillWave />,
      color: "#16a34a",
      bg: "#f0fdf4",
      note: "Filtered total outflow",
    },
  ];

  const updateFilter = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const clearFilters = () => {
    setFilters({ fromDate: "", toDate: "", type: "all", title: "" });
  };

  const downloadExcel = () => {
    const exportRows = filteredTransactions.map((item, index) => ({
      "#": index + 1,
      Date: formatDate(item.trans_date || item.expense_date),
      Type: item.expense_type || "-",
      "Title / Teacher": item.title || "-",
      Amount: Number(item.amount || 0),
    }));

    exportRows.push({
      "#": "",
      Date: "",
      Type: "",
      "Title / Teacher": "Filtered Grand Total",
      Amount: displayTotal,
    });

    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    worksheet["!cols"] = [
      { wch: 6 },
      { wch: 14 },
      { wch: 22 },
      { wch: 32 },
      { wch: 16 },
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Expense Report");
    XLSX.writeFile(workbook, "Expense_Report.xlsx");
  };

  return (
    <div style={styles.page}>
      <div style={styles.shell}>
        <section style={styles.hero}>
          <div style={styles.heroGlow} />
          <div style={styles.heroContent}>
            <div style={styles.heroIcon}>
              <FaChartPie />
            </div>
            <div>
              <h2 style={styles.heroTitle}>Expense Summary Report</h2>
              <p style={styles.heroSubtitle}>
                Track manual expenses, salary expenses, and filtered transaction history.
              </p>
            </div>
          </div>
        </section>

        {loading ? (
          <div style={styles.loadingCard}>Loading expense report...</div>
        ) : (
          <>
            <section style={styles.filterPanel}>
              <div style={styles.filterTitleWrap}>
                <div style={styles.filterIcon}>
                  <FaFilter />
                </div>
                <div>
                  <div style={styles.filterTitle}>Filters</div>
                  <div style={styles.filterSub}>Date wise, type wise and title wise search</div>
                </div>
              </div>

              <div style={styles.filterGrid}>
                <label style={styles.fieldWrap}>
                  <span style={styles.label}>From Date</span>
                  <input
                    type="date"
                    value={filters.fromDate}
                    onChange={(e) => updateFilter("fromDate", e.target.value)}
                    style={styles.input}
                  />
                </label>

                <label style={styles.fieldWrap}>
                  <span style={styles.label}>To Date</span>
                  <input
                    type="date"
                    value={filters.toDate}
                    onChange={(e) => updateFilter("toDate", e.target.value)}
                    style={styles.input}
                  />
                </label>

                <label style={styles.fieldWrap}>
                  <span style={styles.label}>Type</span>
                  <select
                    value={filters.type}
                    onChange={(e) => updateFilter("type", e.target.value)}
                    style={styles.input}
                  >
                    <option value="all">All Types</option>
                    {typeOptions.map((type) => (
                      <option value={type} key={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </label>

                <label style={styles.fieldWrap}>
                  <span style={styles.label}>Title</span>
                  <div style={styles.searchBox}>
                    <FaSearch style={{ color: "#8a99ad" }} />
                    <input
                      value={filters.title}
                      onChange={(e) => updateFilter("title", e.target.value)}
                      placeholder="Search by title"
                      style={styles.searchInput}
                    />
                  </div>
                </label>

                <button type="button" onClick={clearFilters} style={styles.clearButton}>
                  <FaTimes />
                  Clear
                </button>
              </div>
            </section>

            <section style={styles.cardGrid}>
              {cards.map((card) => (
                <div key={card.label} style={styles.metricCard}>
                  <div style={{ ...styles.metricStripe, background: card.color }} />
                  <div style={styles.metricTop}>
                    <div>
                      <div style={styles.metricLabel}>{card.label}</div>
                      <div style={styles.metricValue}>{formatCurrency(card.value)}</div>
                      <div style={styles.metricNote}>{card.note}</div>
                    </div>
                    <div
                      style={{
                        ...styles.metricIcon,
                        background: card.bg,
                        color: card.color,
                        borderColor: `${card.color}24`,
                      }}
                    >
                      {card.icon}
                    </div>
                  </div>
                </div>
              ))}
            </section>

            <section style={styles.contentGrid}>
              <div style={styles.tableCard}>
                <div style={styles.tableHeader}>
                  <div>
                    <h4 style={styles.sectionTitle}>Expense Transaction History</h4>
                    <p style={styles.sectionSub}>
                      Showing {visibleTransactions.length} of {filteredTransactions.length} records
                    </p>
                  </div>
                  <label style={styles.rowsControl}>
                    <span style={styles.rowsLabel}>Show</span>
                    <select
                      value={rowsPerPage}
                      onChange={(e) =>
                        setRowsPerPage(
                          e.target.value === "all" ? "all" : Number(e.target.value)
                        )
                      }
                      style={styles.rowsSelect}
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                      <option value={500}>500</option>
                      <option value="all">All</option>
                    </select>
                  </label>
                  <button
                    type="button"
                    onClick={downloadExcel}
                    disabled={filteredTransactions.length === 0}
                    style={{
                      ...styles.excelButton,
                      ...(filteredTransactions.length === 0 ? styles.disabledButton : {}),
                    }}
                  >
                    <FaFileExcel />
                    Excel
                  </button>
                </div>

                <div style={styles.tableScroll}>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.th}>#</th>
                        <th style={styles.th}>Date</th>
                        <th style={styles.th}>Type</th>
                        <th style={styles.th}>Title / Teacher</th>
                        <th style={{ ...styles.th, textAlign: "right" }}>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleTransactions.length > 0 ? (
                        visibleTransactions.map((item, index) => (
                          <tr key={`${item.id || index}-${item.title || ""}`}>
                            <td style={styles.td}>{index + 1}</td>
                            <td style={styles.td}>{formatDate(item.trans_date || item.expense_date)}</td>
                            <td style={styles.td}>
                              <span style={styles.typePill}>{item.expense_type || "-"}</span>
                            </td>
                            <td style={{ ...styles.td, fontWeight: 800 }}>
                              {item.title || "-"}
                            </td>
                            <td style={{ ...styles.td, ...styles.amountCell }}>
                              {formatCurrency(item.amount)}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="5" style={styles.emptyCell}>
                            No transaction found for the selected filters.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div style={styles.totalCard}>
                <div style={styles.totalLabel}>Filtered Grand Total</div>
                <div style={styles.totalValue}>{formatCurrency(displayTotal)}</div>
                <div style={styles.totalRule} />
                <div style={styles.totalMeta}>
                  Overall total: {formatCurrency(summary.total_expense)}
                </div>
              </div>
            </section>
          </>
        )}
      </div>
    </div>
    
    
    
  );
};

const formatCurrency = (value) =>
  `Rs ${Number(value || 0).toLocaleString("en-IN")}`;

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-IN");
};

const styles = {
  page: {
    width: "100%",
    minHeight: "100vh",
    padding: "14px 18px",
    color: "#172033",
  },
  shell: {
    maxWidth: "1220px",
    margin: "0 auto",
  },
  hero: {
    position: "relative",
    overflow: "hidden",
    padding: "16px 20px",
    marginBottom: 14,
    background:
      "linear-gradient(135deg, rgba(255,255,255,0.98), rgba(240,249,255,0.96))",
    border: "1px solid rgba(37,99,235,0.12)",
    borderRadius: 12,
    boxShadow: "0 10px 26px rgba(23,32,51,0.08)",
  },
  heroGlow: {
    position: "absolute",
    inset: 0,
    background:
      "radial-gradient(circle at 12% 0%, rgba(37,99,235,0.1), transparent 28%), radial-gradient(circle at 88% 0%, rgba(22,163,74,0.1), transparent 26%)",
    pointerEvents: "none",
  },
  heroContent: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  heroIcon: {
    width: 42,
    height: 42,
    borderRadius: 11,
    display: "grid",
    placeItems: "center",
    color: "#2563eb",
    background: "#eff6ff",
    border: "1px solid rgba(37,99,235,0.16)",
    fontSize: 20,
    flexShrink: 0,
  },
  heroTitle: {
    margin: 0,
    color: "#102a43",
    fontWeight: 950,
    fontSize: 24,
  },
  heroSubtitle: {
    margin: "4px 0 0",
    color: "#607086",
    fontWeight: 650,
    fontSize: 13,
  },
  loadingCard: {
    padding: 48,
    textAlign: "center",
    background: "#fff",
    border: "1px solid rgba(78,102,135,0.16)",
    borderRadius: 14,
    boxShadow: "0 12px 30px rgba(23,32,51,0.08)",
    color: "#607086",
    fontWeight: 800,
  },
  filterPanel: {
    padding: 14,
    marginBottom: 14,
    background: "#fff",
    border: "1px solid rgba(78,102,135,0.16)",
    borderRadius: 12,
    boxShadow: "0 8px 22px rgba(23,32,51,0.07)",
  },
  filterTitleWrap: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: 12,
  },
  filterIcon: {
    width: 36,
    height: 36,
    borderRadius: 10,
    display: "grid",
    placeItems: "center",
    color: "#2563eb",
    background: "#eff6ff",
  },
  filterTitle: {
    color: "#102a43",
    fontWeight: 900,
    fontSize: 16,
  },
  filterSub: {
    color: "#607086",
    fontWeight: 650,
    fontSize: 13,
  },
  filterGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 10,
    alignItems: "end",
  },
  fieldWrap: {
    display: "flex",
    flexDirection: "column",
    gap: 7,
  },
  label: {
    color: "#607086",
    fontWeight: 850,
    fontSize: 12,
  },
  input: {
    width: "100%",
    minHeight: 40,
    border: "1px solid rgba(78,102,135,0.18)",
    borderRadius: 10,
    background: "#f8fbff",
    padding: "0 12px",
    color: "#172033",
    outline: "none",
    fontWeight: 700,
  },
  searchBox: {
    minHeight: 40,
    border: "1px solid rgba(78,102,135,0.18)",
    borderRadius: 10,
    background: "#f8fbff",
    padding: "0 12px",
    display: "flex",
    alignItems: "center",
    gap: 9,
  },
  searchInput: {
    width: "100%",
    border: 0,
    outline: 0,
    background: "transparent",
    color: "#172033",
    fontWeight: 700,
  },
  clearButton: {
    minHeight: 40,
    border: "1px solid rgba(225,29,72,0.18)",
    borderRadius: 10,
    background: "#fff1f2",
    color: "#be123c",
    fontWeight: 900,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    cursor: "pointer",
  },
  cardGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))",
    gap: 12,
    marginBottom: 14,
  },
  metricCard: {
    position: "relative",
    overflow: "hidden",
    padding: 16,
    background: "#fff",
    border: "1px solid rgba(78,102,135,0.16)",
    borderRadius: 12,
    boxShadow: "0 8px 22px rgba(23,32,51,0.08)",
  },
  metricStripe: {
    position: "absolute",
    top: 0,
    bottom: 0,
    left: 0,
    width: 4,
  },
  metricTop: {
    display: "flex",
    justifyContent: "space-between",
    gap: 14,
  },
  metricLabel: {
    color: "#607086",
    fontWeight: 850,
    fontSize: 13,
  },
  metricValue: {
    marginTop: 5,
    color: "#172033",
    fontSize: 23,
    fontWeight: 950,
    lineHeight: 1.1,
  },
  metricNote: {
    marginTop: 5,
    color: "#607086",
    fontWeight: 650,
    fontSize: 13,
  },
  metricIcon: {
    width: 44,
    height: 44,
    borderRadius: 12,
    display: "grid",
    placeItems: "center",
    border: "1px solid",
    fontSize: 20,
    flexShrink: 0,
  },
  contentGrid: {
    display: "block",
  },
  tableCard: {
    overflow: "hidden",
    background: "#fff",
    border: "1px solid rgba(78,102,135,0.16)",
    borderRadius: 12,
    boxShadow: "0 8px 22px rgba(23,32,51,0.08)",
  },
  tableHeader: {
    padding: "12px 16px",
    borderBottom: "1px solid rgba(78,102,135,0.14)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
  },
  sectionTitle: {
    margin: 0,
    color: "#102a43",
    fontWeight: 950,
  },
  sectionSub: {
    margin: "5px 0 0",
    color: "#607086",
    fontWeight: 650,
  },
  rowsControl: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 8px 6px 10px",
    background: "#f8fbff",
    border: "1px solid rgba(78,102,135,0.16)",
    borderRadius: 10,
    boxShadow: "0 4px 12px rgba(23,32,51,0.04)",
  },
  rowsLabel: {
    color: "#607086",
    fontSize: 12,
    fontWeight: 850,
  },
  rowsSelect: {
    height: 28,
    minWidth: 72,
    border: 0,
    outline: "none",
    background: "transparent",
    color: "#172033",
    fontSize: 13,
    fontWeight: 900,
    cursor: "pointer",
  },
  excelButton: {
    minHeight: 40,
    padding: "0 14px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    border: 0,
    borderRadius: 10,
    color: "#fff",
    background: "#15803d",
    fontSize: 13,
    fontWeight: 900,
    cursor: "pointer",
    boxShadow: "0 8px 18px rgba(21,128,61,0.18)",
  },
  disabledButton: {
    opacity: 0.55,
    cursor: "not-allowed",
    boxShadow: "none",
  },
  tableScroll: {
    overflowX: "auto",
  },
  table: {
    width: "100%",
    minWidth: 720,
    borderCollapse: "collapse",
  },
  th: {
    padding: "11px 14px",
    color: "#607086",
    background: "#f5f8fc",
    fontSize: 12,
    fontWeight: 950,
    textTransform: "uppercase",
    borderBottom: "1px solid rgba(78,102,135,0.14)",
    textAlign: "left",
  },
  td: {
    padding: "10px 14px",
    color: "#172033",
    borderBottom: "1px solid rgba(78,102,135,0.1)",
  },
  typePill: {
    display: "inline-flex",
    padding: "5px 10px",
    borderRadius: 999,
    color: "#2563eb",
    background: "#eff6ff",
    fontWeight: 900,
    fontSize: 12,
  },
  amountCell: {
    textAlign: "right",
    fontWeight: 950,
    color: "#dc2626",
  },
  emptyCell: {
    padding: 36,
    color: "#607086",
    textAlign: "center",
    fontWeight: 800,
  },
  totalCard: {
    marginTop: 12,
    padding: "14px 18px",
    background: "#fff",
    border: "1px solid rgba(78,102,135,0.16)",
    borderRadius: 12,
    boxShadow: "0 8px 22px rgba(23,32,51,0.08)",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    textAlign: "left",
  },
  totalLabel: {
    color: "#607086",
    fontWeight: 900,
    fontSize: 13,
  },
  totalValue: {
    marginTop: 0,
    color: "#dc2626",
    fontSize: 28,
    lineHeight: 1.1,
    fontWeight: 950,
  },
  totalRule: {
    display: "none",
    margin: 0,
    width: 0,
    height: 0,
    borderRadius: 999,
    background: "#d7e3f1",
  },
  totalMeta: {
    marginTop: 0,
    color: "#607086",
    fontWeight: 700,
  },
};

export default ExpenseReportPage;
